import type { ScopeRef } from "@enduring-systems/entitlements";
import { scopeKey } from "@enduring-systems/entitlements";
import { useEffect, useState } from "react";

import { type EffectiveVsGranted, readEffective } from "./entitlements-api";

/**
 * The scope id is free text, so the selection changes on every keystroke. The read
 * is a staff-audited cross-tenant POST, and an intermediate prefix can name a real
 * scope, so the request waits for the typing to settle.
 */
export const READ_DEBOUNCE_MS = 300;

export interface UseEffectiveReturn {
  view: EffectiveVsGranted | null;
  isLoading: boolean;
  error: string | null;
}

interface ReadState {
  /** The selection this state describes. State for any other one is not an answer. */
  readonly selection: string;
  readonly view: EffectiveVsGranted | null;
  readonly error: string | null;
  readonly isLoading: boolean;
}

function selectionOf(scope: ScopeRef | null, capabilityId: string, reloadKey: number): string {
  return `${scope === null ? "none" : scopeKey(scope)}|${capabilityId}|${reloadKey}`;
}

/**
 * Read effective-vs-granted for the selected scope + capability, re-reading when
 * `reloadKey` changes so a write refreshes the reading it just invalidated.
 *
 * Every result is stored WITH the selection it answers, and a result for any other
 * selection is discarded on read. That is what makes a stale reading impossible
 * rather than merely unlikely: a request-id guard has to be invalidated somewhere,
 * and anything that invalidates it in an effect leaves a window (effects run after
 * paint) in which a late response for the previous scope is still accepted. A value
 * attributed to the wrong scope is worse than no value, and here it would also seed
 * the grant editor, so the answer has to carry its own question.
 *
 * `scope` must be referentially stable across renders (the page memoizes it).
 */
export function useEffective(
  scope: ScopeRef | null,
  capabilityId: string,
  reloadKey: number
): UseEffectiveReturn {
  const selection = selectionOf(scope, capabilityId, reloadKey);
  const [state, setState] = useState<ReadState>({
    selection,
    view: null,
    error: null,
    isLoading: false,
  });

  useEffect(() => {
    if (scope === null || capabilityId === "") {
      setState({ selection, view: null, error: null, isLoading: false });
      return;
    }

    setState({ selection, view: null, error: null, isLoading: true });
    const timer = setTimeout(() => {
      readEffective({ scope, capabilityId })
        .then((next) => {
          setState({ selection, view: next, error: null, isLoading: false });
        })
        .catch((e: unknown) => {
          setState({
            selection,
            view: null,
            error: e instanceof Error ? e.message : "Failed to read the effective value",
            isLoading: false,
          });
        });
    }, READ_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [scope, capabilityId, reloadKey, selection]);

  if (state.selection === selection) {
    return { view: state.view, isLoading: state.isLoading, error: state.error };
  }
  // The selection changed and the effect has not run yet: report the read as
  // pending rather than handing back the previous selection's answer.
  return { view: null, error: null, isLoading: scope !== null && capabilityId !== "" };
}
